/**
 * Where a directional light's shadow map goes.
 *
 * PlayCanvas fits a directional shadow map to the camera's view frustum (up to shadowDistance),
 * so the map moves with every camera move and must be re-rendered every frame, and shadows
 * further than shadowDistance from the camera disappear. Here the map is fitted to the shadow
 * casters instead: seen from the light (an orthographic projection), a caster at (x, y) can only
 * shadow points behind it at the same (x, y), so the casters' footprint in light space is all the
 * map has to cover. That fit only changes when the light turns or a caster moves, which is what
 * lets the map render once and be reused while the camera moves (see shadowSystem.ts).
 *
 * Receivers further from the light than the casters still read correctly: their depth clamps to
 * 1 and is compared against the casters' depths.
 */
import * as pc from 'playcanvas';

export interface ShadowFit {
    position: pc.Vec3;
    rotation: pc.Quat;
    orthoHeight: number;
    nearClip: number;
    farClip: number;
}

/** The shadow camera looks down the light's -Y: the light rotation turned -90 degrees about X (as the engine does). */
const CAMERA_FROM_LIGHT = new pc.Quat().setFromEulerAngles(-90, 0, 0);
/** Size steps for the fit, so small caster motion keeps the same texel grid (no shimmering). */
const SIZE_STEP = 1.1;
const MIN_ORTHO_HEIGHT = 0.05;

const _inv = new pc.Quat();
const _p = new pc.Vec3();

/**
 * Fit an orthographic shadow camera around `boxes` (world-space caster bounds) for a light with
 * world rotation `lightRotation`. Returns null when there is nothing to cover.
 * `resolution` is the shadow map size in texels; `marginTexels` keeps casters (and the filter
 * kernel) away from the map's clamped edge.
 */
export function fitDirectionalShadow(
    lightRotation: pc.Quat,
    boxes: ReadonlyArray<pc.BoundingBox>,
    resolution: number,
    marginTexels = 6,
): ShadowFit | null {
    if (boxes.length === 0) return null;
    const rotation = new pc.Quat().mul2(lightRotation, CAMERA_FROM_LIGHT);
    _inv.copy(rotation).invert();
    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
    for (const box of boxes) {
        const c = box.center, h = box.halfExtents;
        if (![c.x, c.y, c.z, h.x, h.y, h.z].every(Number.isFinite)) continue;
        for (let i = 0; i < 8; i++) {
            _p.set(c.x + (i & 1 ? h.x : -h.x), c.y + (i & 2 ? h.y : -h.y), c.z + (i & 4 ? h.z : -h.z));
            _inv.transformVector(_p, _p);
            if (_p.x < minX) minX = _p.x; if (_p.x > maxX) maxX = _p.x;
            if (_p.y < minY) minY = _p.y; if (_p.y > maxY) maxY = _p.y;
            if (_p.z < minZ) minZ = _p.z; if (_p.z > maxZ) maxZ = _p.z;
        }
    }
    if (!Number.isFinite(minX)) return null;
    const half = Math.max(maxX - minX, maxY - minY, 2 * MIN_ORTHO_HEIGHT) / 2;
    const withMargin = half * (1 + (2 * marginTexels) / resolution);
    const orthoHeight = Math.pow(SIZE_STEP, Math.ceil(Math.log(withMargin) / Math.log(SIZE_STEP)));
    // Snap the centre to the texel grid, so the map does not shift by sub-texel amounts.
    const texel = (2 * orthoHeight) / resolution;
    const cx = Math.round((minX + maxX) / 2 / texel) * texel;
    const cy = Math.round((minY + maxY) / 2 / texel) * texel;
    const depth = maxZ - minZ;
    const pad = 0.05 + depth * 0.02;
    const position = new pc.Vec3(cx, cy, maxZ + pad);
    rotation.transformVector(position, position);
    return { position, rotation, orthoHeight, nearClip: 0.01, farClip: depth + 2 * pad };
}

/** A compact identity for a fit, for change detection (equal keys = same shadow map placement). */
export function shadowFitKey(fit: ShadowFit | null): string {
    if (!fit) return 'none';
    const r = (v: number) => Math.round(v * 1e4) / 1e4;
    const { position: p, rotation: q } = fit;
    return [p.x, p.y, p.z, q.x, q.y, q.z, q.w, fit.orthoHeight, fit.farClip].map(r).join(',');
}

const _view = new pc.Mat4();
const _proj = new pc.Mat4();
const _viewProj = new pc.Mat4();
const _viewport = new pc.Mat4().setViewport(0, 0, 1, 1);

/**
 * The shadow matrix (world → map uv and depth) the engine computes when it renders a map placed
 * by `fit` (ShadowRenderer.dispatchUniforms for a single-cascade directional light: viewport ×
 * orthographic projection × the camera's inverse transform). Lets a receiver use the matrix of a
 * map that is rendered later in the same frame.
 */
export function fitShadowMatrix(fit: ShadowFit, out = new pc.Mat4()): pc.Mat4 {
    _view.setTRS(fit.position, fit.rotation, pc.Vec3.ONE).invert();
    const h = fit.orthoHeight;
    _proj.setOrtho(-h, h, -h, h, fit.nearClip, fit.farClip);
    _viewProj.mul2(_proj, _view);
    return out.mul2(_viewport, _viewProj);
}
